// shared/case-studies-data.ts

import { featureFlags } from "@/config/featureFlags";

// Centralized card IDs - single source of truth
export enum CardId {
  NSW_WATER_COMPLIANCE = "nsw-water-compliance",
  VMAP_VEGETATION_MONITORING = "vmap-vegetation-monitoring",
  ACT_ENVIRONMENTAL_ASSESSMENT = "act-environmental-assessment",
  AUSTRALIA_ENVIRONMENT_REPORT = "australia-environment-report",
  PLANR_PLATFORM = "planr-platform",
  OZHAZARD_BUSHFIRE_INTELLIGENCE = "ozhazard-bushfire-intelligence",
}

export interface BaseSolutionCard {
  id: CardId;
  title: string;
  subtitle: string;
  description: string;
  image: string;
  stats: { value: string; label: string }[];
  link: string;
  visible: boolean;
  publishedDate: string;
}

export interface GridSolutionCard extends BaseSolutionCard {
  bgColor: string;
  statBgColor: string;
  className: string;
}

// Base data - single source of truth (all stories including feature-flagged ones)
const allBaseSolutionCards: BaseSolutionCard[] = [
  {
    id: CardId.NSW_WATER_COMPLIANCE,
    title: "Water license compliance monitoring in NSW",
    subtitle: "Government Water Authority",
    description:
      "Transformed NSW water compliance from manual inspections to satellite-based screening, enabling proactive monitoring of 10,000+ agricultural properties for potential non-compliance.",
    image: "/images/nsw-water-compliance.webp",
    stats: [
      { value: "10,000+", label: "Irrigation Properties" },
      { value: "3,600", label: "Site Visits in 2023/24" },
      { value: "368", label: "Enforcement Actions" },
    ],
    link: "/case-studies/nsw-water-compliance",
    visible: featureFlags.nsw_water_compliance,
    publishedDate: "2026-06-01",
  },
  {
    id: CardId.ACT_ENVIRONMENTAL_ASSESSMENT,
    title: "Evidence-based environmental policy in the ACT",
    subtitle: "ACT Government Sustainability",
    description:
      "20-year satellite analysis of urban growth impacts on the ACT's ecosystems and threatened species, directly informing 15 policy recommendations for environmental protection.",
    stats: [
      { value: "20 Years", label: "Environmental Trends" },
      { value: "40%", label: "Increase in Building Footprint" },
      { value: "15", label: "Policy Recommendations" },
    ],
    image: "/images/aerial-view-of-australian-suburb.webp",
    link: "/case-studies/act-environmental-assessment",
    visible: true,
    publishedDate: "2026-05-01",
  },
  {
    id: CardId.AUSTRALIA_ENVIRONMENT_REPORT,
    title: "Annual environmental reporting across Australia",
    subtitle: "TERN & ANU Environmental Monitoring",
    description:
      "Powering Australia's Environment Report with complete data infrastructure - transforming continental-scale monitoring from supercomputer batch processing to on-demand analytics accessible to researchers, managers, and the public.",
    stats: [
      { value: "25+ Years", label: "Satellite Observations" },
      { value: "20+ TB", label: "Spatial Data" },
      { value: "10 Million", label: "Readers" },
    ],
    image: "/images/aus-env-report-2.webp",
    link: "/case-studies/australia-environment-report",
    visible: true,
    publishedDate: "2026-04-01",
  },
  {
    id: CardId.PLANR_PLATFORM,
    title: "Enabling Australia's Nature Repair Market",
    subtitle: "Australian Government",
    description:
      "Powering Australia's world-first Nature Repair Market with real-time environmental impact calculations, enabling landholders to instantly assess biodiversity and carbon benefits of proposed projects anywhere in Australia.",
    stats: [
      { value: "100%", label: "National Coverage" },
      { value: "Free", label: "C+B Assessments" },
      { value: "1,000+", label: "Monthly Users" },
    ],
    image: "/images/dam-water.webp",
    link: "/case-studies/planr-platform",
    visible: true,
    publishedDate: "2026-03-01",
  },
  {
    id: CardId.OZHAZARD_BUSHFIRE_INTELLIGENCE,
    title: "Continental bushfire intelligence with an AI agent",
    subtitle: "OzHazard — Terrakio Agent in production",
    description:
      "An always-on AI agent watching Australia for wildfire: fusing FIRMS thermal detections, Himawari imagery, lightning feeds and agency records into named, correlated incidents — published live to a public dashboard and a government-ready API.",
    stats: [
      { value: "Always-On", label: "Autonomous Agent" },
      { value: "7.7M km²", label: "Continental Coverage" },
      { value: "Live", label: "Signal Fusion" },
    ],
    image: "/images/ozhazard-bushfire.webp",
    link: "/case-studies/ozhazard-bushfire-intelligence",
    visible: featureFlags.ozhazard_bushfire_intelligence,
    publishedDate: "2026-07-14",
  },
  {
    id: CardId.VMAP_VEGETATION_MONITORING,
    title: "Monitoring Australia's changing tree and forest cover",
    subtitle: "VMAP — ANU & Haizea Analytics",
    description:
      "An annually updated, AI-driven map of forest extent and loss across Australia since 2020 — trained on airborne LiDAR, powered by Terrak.io over the full Sentinel-2 archive, freely accessible for non-commercial use.",
    stats: [
      { value: "10 m", label: "Pixel Resolution" },
      { value: "2020+", label: "Annual Updates" },
      { value: "7.7M km²", label: "National Coverage" },
    ],
    image: "/images/vmap.webp",
    link: "/case-studies/vmap-vegetation-monitoring",
    visible: featureFlags.vmap_vegetation_monitoring,
    publishedDate: "2026-01-01",
  },
];

// Grid-specific styling config (colors only)
const gridStyles: Record<CardId, { bgColor: string; statBgColor: string }> = {
  [CardId.NSW_WATER_COMPLIANCE]: {
    bgColor: "bg-haizea-blue/60",
    statBgColor: "bg-haizea-blue/30",
  },
  [CardId.ACT_ENVIRONMENTAL_ASSESSMENT]: {
    bgColor: "bg-haizea-orange/60",
    statBgColor: "bg-haizea-orange/30",
  },
  [CardId.PLANR_PLATFORM]: {
    bgColor: "bg-haizea-green/60",
    statBgColor: "bg-haizea-green/30",
  },
  [CardId.AUSTRALIA_ENVIRONMENT_REPORT]: {
    bgColor: "bg-haizea-green/60",
    statBgColor: "bg-haizea-green/30",
  },
  [CardId.OZHAZARD_BUSHFIRE_INTELLIGENCE]: {
    bgColor: "bg-haizea-orange/60",
    statBgColor: "bg-haizea-orange/30",
  },
  [CardId.VMAP_VEGETATION_MONITORING]: {
    bgColor: "bg-haizea-green/60",
    statBgColor: "bg-haizea-green/30",
  },
};

// Filter visible cards and sort by publishedDate descending (newest first)
export const baseSolutionCards: BaseSolutionCard[] = allBaseSolutionCards
  .filter((card) => card.visible)
  .sort(
    (a, b) =>
      new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
  );

// Derived arrays
export const caseStudiesGridCards = baseSolutionCards; // Same order as base

// Dynamic grid layout based on visible card position
export const realWorldSolutionsCards: GridSolutionCard[] = baseSolutionCards
  .slice(0, 4)
  .map((card, index) => {
    let className = "h-84";

    // First visible card: large grid (spans 2 cols/rows on lg)
    if (index === 0) {
      className = "h-84 md:h-auto lg:col-span-2 lg:row-span-2";
    }
    // Second visible card: spans 2 cols on lg
    else if (index === 1) {
      className = "h-84 lg:col-span-2";
    }

    return { ...card, ...gridStyles[card.id], className };
  });
